// ClientTransactions.js
import { avisarSaldoCambiado } from '../../../lib/saldoBus';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import React, { useContext, useState } from "react";
import { formatApiDateTime, parseApiDateTime } from "../../../lib/helpers";
import { UserContext } from "../../../context/UserContext";
import { HiOutlineTrash } from 'react-icons/hi';
import QuantityCounter from '../../Elements/QuantityCounter';
import { etiquetaCantidadLinea, lineaAPeso, notaPedidoMerma } from '../../../lib/helpers/products';

/** El nombre de una línea, el MISMO en la fila, en la papelera y en el diálogo. */
const nombreLinea = (item) =>
  item?.product_name || item?.productName || item?.ProductName || 'Producto desconocido';

function ClientTransactions({ transactions, clientId, bare = false, onFeeReversed, onChanged }) {
  const [token] = useContext(UserContext);
  // Cuotas anuladas en esta sesión: se ocultan al momento sin esperar al refetch.
  const [reversedIds, setReversedIds] = useState(() => new Set());
  const [reversingId, setReversingId] = useState(null);

  const isMembershipFee = (tx) => tx.concept === 'membership_fee';

  // Anular la cuota de inscripción (casilla marcada por error en el alta):
  // devuelve el importe al saldo del socio y marca la transacción reversed.
  // ConfirmDialog y no window.confirm: en las tablets el nativo se traga, así
  // que este botón —que mueve DINERO— estaba muerto ahí y sin decir nada.
  const [porAnular, setPorAnular] = React.useState(null);

  const reverseFee = async (tx) => {
    setPorAnular(null);
    setReversingId(tx.id);
    try {
      const res = await fetch(`/v1/reverse-inscription-fee/${tx.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Error ${res.status}`);
      setReversedIds((prev) => new Set(prev).add(tx.id));
      // Anular una cuota DEVUELVE dinero al saldo, y hasta ahora ese número se
      // tiraba: la respuesta trae `new_balance` y nadie la leía, así que la
      // cabecera de la ficha y el POS abierto al lado se quedaban con el saldo
      // viejo hasta recargar. Ojo con Number(null) === 0: el null se corta antes.
      const nb = data?.new_balance != null ? Number(data.new_balance) : NaN;
      if (Number.isFinite(nb) && clientId) avisarSaldoCambiado(clientId, nb);
      if (onFeeReversed) onFeeReversed(tx);
    } catch (e) {
      window.alert(e.message || 'No se pudo anular la cuota.');
    } finally {
      setReversingId(null);
    }
  };

  // Quitar productos de una venta completada — el "marqué uno de más" (Kong
  // Club, 14-ago). El backend devuelve stock e importe según el método de pago.
  //
  // UNA papelera en toda línea, a granel o por unidades (reporte Kong House,
  // 12-10-2026). Antes las de unidades llevaban un «−1u» sin icono que nadie
  // leía como borrar: el club creyó que solo Contabilidad podía quitarlas. Por
  // unidades se elige cuántas (por defecto 1); a granel se quita la línea entera.
  // Confirma siempre, con ConfirmDialog: mueve dinero y stock, y el nativo se
  // traga en las tablets.
  const [porQuitar, setPorQuitar] = useState(null);    // { tx, item }
  const [unidades, setUnidades] = useState('1');
  // Error del servidor (no se quitó nada): se dice DENTRO del diálogo. Nada de
  // window.alert: en las tablets se traga igual que el confirm.
  const [errorQuitar, setErrorQuitar] = useState('');
  // Sin respuesta (red caída, timeout): NO sabemos si se quitó. Se cierra el
  // diálogo y se recarga el historial en vez de dejar «Quitar» a mano para un
  // segundo toque que quitaría otra unidad más (el reintento de dinero).
  const [aviso, setAviso] = useState('');

  const pedirQuitar = (tx, item) => {
    setUnidades('1');
    setErrorQuitar('');
    setAviso('');
    setPorQuitar({ tx, item });
  };

  const maxUnidades = (item) => Math.max(1, Math.floor(Number(item?.quantity) || 0));

  // Lo tecleado se recorta al rango de la línea AL ESCRIBIR, no al enviar: así
  // el contador enseña siempre lo que se va a quitar («9» en una línea de 3
  // pasa a 3; un 0 deja el campo vacío, que vale 1).
  const cambiarUnidades = (item) => (v) => {
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n <= 0) return setUnidades('');
    setUnidades(String(Math.min(n, maxUnidades(item))));
  };

  const cantidadAQuitar = (item) => {
    if (lineaAPeso(item)) return parseFloat(item.quantity || 0);
    const n = parseInt(unidades, 10) || 1;
    return Math.min(Math.max(n, 1), maxUnidades(item));
  };

  const reverseUnits = async () => {
    if (!porQuitar) return;
    const { tx, item } = porQuitar;
    const qty = cantidadAQuitar(item);
    setReversingId(tx.id);
    setErrorQuitar('');
    let res;
    try {
      res = await fetch(`/v1/reverse-purchase-item/${tx.id}/${item.id}?quantity=${qty}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      setPorQuitar(null);
      setAviso('No llegó la respuesta del servidor. Hemos recargado el historial: compruébalo antes de volver a quitar.');
      if (onChanged) onChanged(null);
      setReversingId(null);
      return;
    }
    const data = await res.json().catch(() => ({}));
    setReversingId(null);
    if (!res.ok) {
      setErrorQuitar(data?.detail || `No se pudo quitar (error ${res.status}).`);
      return;
    }
    // El backend devuelve new_balance: se propaga para que la ficha y las
    // cachés del padre reflejen el saldo al momento, sin recargar página.
    // Anular una cuota o una venta MUEVE el saldo, así que va al bus como
    // cualquier otro movimiento económico: si no, la cabecera de la ficha y
    // el POS abierto en otra pestaña se quedaban con el número viejo hasta
    // recargar. Ojo con Number(null) === 0: el null se corta antes.
    const nb = data?.new_balance != null ? Number(data.new_balance) : NaN;
    if (Number.isFinite(nb) && clientId) avisarSaldoCambiado(clientId, nb);
    setPorQuitar(null);
    if (onChanged) onChanged(data);
  };

  const textoQuitar = (item) => {
    if (!item) return '';
    const nombre = nombreLinea(item);
    if (lineaAPeso(item)) {
      return `Se elimina la línea de ${nombre} (${etiquetaCantidadLinea(item)}). Se devuelve el stock y el importe según cómo se pagó.`;
    }
    const n = cantidadAQuitar(item);
    return `Se quita${n === 1 ? '' : 'n'} ${n} u de ${nombre}. Se devuelve el stock y el importe según cómo se pagó.`;
  };

  const validTransactions = Array.isArray(transactions)
    ? transactions.filter((t) => t.status !== 'reversed' && !reversedIds.has(t.id))
    : [];
  const filtered = clientId
    ? validTransactions.filter((t) => t.user_id === clientId)
    : [];
  const sorted = [...filtered].sort(
    (a, b) => parseApiDateTime(b.timestamp) - parseApiDateTime(a.timestamp)
  );

  // Método de pago: clave para cuadrar caja (solo el efectivo entra en ella).
  // 'credit' = compra pagada con el saldo del socio. Guardado desde 04-2026;
  // transacciones más antiguas sin método muestran «—».
  const paymentBadge = (method) => {
    const key = String(method || '').toLowerCase();
    const map = {
      cash:   { label: 'Efectivo', cls: 'bg-lime-100 text-lime-800' },
      card:   { label: 'Tarjeta',  cls: 'bg-blue-100 text-blue-800' },
      credit: { label: 'Saldo',    cls: 'bg-gray-100 text-gray-700' },
    };
    const item = map[key];
    if (!item) return <span className="text-gray-400">—</span>;
    return (
      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] md:text-xs font-medium ${item.cls}`}>
        {item.label}
      </span>
    );
  };

  // Función para traducir tipos de transacción
  const translateTransactionType = (type, concept) => {
    if (concept === 'membership_fee') return 'Cuota de inscripción';
    const typeStr = String(type || '').toLowerCase();
    const translations = {
      'sale': 'Venta',
      'purchase': 'Venta',
      'add_cash': 'Añadir Crédito',
      'refund': 'Reembolso',
      'reverse_add_cash': 'Reversar Crédito'
    };
    return translations[typeStr] || type || 'Desconocido';
  };

  if (!clientId) {
    return <p className="text-center text-gray-500">No hay cliente seleccionado.</p>;
  }

  return (
    <div className={bare ? "overflow-x-auto" : "bg-white p-4 md:p-6 rounded-lg shadow border border-gray-200 overflow-x-auto"}>
      {!bare && (
        <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">
          Historial de Movimientos
        </h2>
      )}
      {aviso && (
        <p role="status" className="mb-3 rounded-lg border border-[var(--destructive-border)] bg-[var(--destructive-bg)] px-3 py-2 text-xs text-[var(--toast-error-text)]">
          {aviso}
        </p>
      )}
      <table className="min-w-full table-auto text-xs md:text-sm">
        {/* Compacta a propósito, y con el px reducido del móvil. */}
        <TableHead>
          <Th className="px-2 py-2 sm:px-4">Fecha</Th>
          <Th className="px-2 py-2 sm:px-4">Tipo</Th>
          <Th className="px-2 py-2 sm:px-4">Pago</Th>
          <Th className="px-2 py-2 sm:px-4">Productos</Th>
          <Th align="right" className="px-2 py-2 sm:px-4">Cantidad</Th>
        </TableHead>
        <tbody>
          {sorted.map((tx) => tx._visita ? (
            /* Visita al club (plan CRUO §J). Va en el MISMO historial que el
               dinero a propósito: quien abre esta pantalla quiere saber qué ha
               pasado con este socio, y una entrada es tan parte de eso como una
               compra. Sin importe, sin método de pago y sin productos — poner
               «0,00 €» sería decir que se cobró algo. */
            <Tr key={tx.id}>
              <Td className="whitespace-nowrap px-2 py-2 sm:px-4">{formatApiDateTime(tx.timestamp)}</Td>
              {/* Entrada y salida JUNTAS, en la columna del concepto. Antes la
                  salida vivía en la columna «Productos» —donde no pinta nada— y
                  la entrada quedaba suelta como fecha, así que la visita no se
                  leía como lo que es: un tramo, de una hora a otra. */}
              {/* El celeste de la visita se queda: es lo único que distingue de un
                  vistazo una entrada de un movimiento de dinero en la misma lista.
                  Es paleta categórica y el sistema aún no la tiene. */}
              <Td className="px-2 py-2 sm:px-4" colSpan={3}>
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400" aria-hidden="true" />
                  <span>Visita al club</span>
                  <span className="tabular-nums text-[var(--muted-foreground)]">
                    {tx._entrada}
                    {tx._salida ? ` → ${tx._salida}` : ''}
                  </span>
                  {!tx._salida && (
                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
                      En sala
                    </span>
                  )}
                </span>
              </Td>
              <Td align="right" className="px-2 py-2 sm:px-4 text-[var(--muted-foreground)]">—</Td>
            </Tr>
          ) : (
            <Tr key={tx.id}>
              <Td className="whitespace-nowrap px-2 py-2 sm:px-4">
                {formatApiDateTime(tx.timestamp)}
              </Td>
              <Td className="px-2 py-2 sm:px-4">
                {translateTransactionType(tx.type, tx.concept)}
                {isMembershipFee(tx) && (
                  <button
                    onClick={() => setPorAnular(tx)}
                    disabled={reversingId === tx.id}
                    className="ml-2 rounded-sm border border-[var(--destructive-border)] px-2 py-0.5 text-[10px] font-medium text-[var(--destructive)] hover:bg-[var(--destructive-bg)] disabled:opacity-50 md:text-xs"
                    title="Anular la cuota y devolver el importe al saldo"
                  >
                    {reversingId === tx.id ? 'Anulando…' : 'Anular'}
                  </button>
                )}
              </Td>
              <Td className="px-2 py-2 sm:px-4">{paymentBadge(tx.payment_method)}</Td>

              <Td className="px-2 py-2 sm:px-4">
                {(() => {
                  // Verificar si hay items - múltiples formas de verificar
                  const items = tx.items || tx.Items || [];
                  const hasItems = Array.isArray(items) && items.length > 0;

                  if (hasItems) {
                    return (
                      <div className="space-y-1">
                        {items.map((item, i) => {
                          const productName = nombreLinea(item);

                          const esVenta = String(tx.type || '').toLowerCase() === 'purchase';
                          return (
                            <div key={item.id || i} className="text-xs flex items-center gap-1">
                              <span className="font-medium">{productName}</span>
                              {/* Lo DESPACHADO (báscula si la hubo); lo pedido y la merma, de nota. */}
                              <span className="ml-1 tabular-nums text-[var(--muted-foreground)]">{etiquetaCantidadLinea(item)}</span>
                              {notaPedidoMerma(item) && (
                                <span className="ml-1 text-[var(--muted-foreground)]">· {notaPedidoMerma(item)}</span>
                              )}
                              {esVenta && item.id && !isMembershipFee(tx) && (
                                <button
                                  type="button"
                                  onClick={() => pedirQuitar(tx, item)}
                                  disabled={reversingId === tx.id}
                                  aria-label={`Quitar ${productName}`}
                                  title="Quitar de la venta (devuelve stock e importe)"
                                  className="-my-2 ml-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-[var(--destructive)] hover:bg-[var(--destructive-bg)] disabled:opacity-50"
                                >
                                  <HiOutlineTrash className="h-4 w-4" aria-hidden="true" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  } else {
                    return (
                      <span className="text-xs italic text-[var(--muted-foreground)]">
                        {tx.type === 'purchase' || tx.type === 'PURCHASE' ? 'Sin productos' : '-'}
                      </span>
                    );
                  }
                })()}
              </Td>

              <Td align="right" className={`px-2 py-2 sm:px-4 font-semibold ${String(tx.type || '').toLowerCase() === 'add_cash' ? 'text-[var(--success)]' :
                  String(tx.type || '').toLowerCase() === 'refund' || String(tx.type || '').toLowerCase() === 'reverse_add_cash' ? 'text-[var(--destructive)]' :
                    'text-[var(--foreground)]'
                }`}>
                {String(tx.type || '').toLowerCase() === 'add_cash' ? '+' :
                  String(tx.type || '').toLowerCase() === 'refund' || String(tx.type || '').toLowerCase() === 'reverse_add_cash' ? '-' : ''}
                €{Math.abs(tx.amount || 0).toFixed(2)}
              </Td>
            </Tr>
          ))}
          {sorted.length === 0 && (
            <tr>
              <Td align="center" colSpan={5} className="py-4 italic text-[var(--muted-foreground)]">
                No se encontraron movimientos.
              </Td>
            </tr>
          )}
        </tbody>
      </table>
      <ConfirmDialog
        active={porAnular != null}
        title="Anular la cuota"
        description="Se anulará la cuota y el importe vuelve al saldo del socio."
        confirmLabel="Anular la cuota"
        tone="danger"
        busy={reversingId === porAnular?.id}
        onConfirm={() => porAnular && reverseFee(porAnular)}
        onCancel={() => setPorAnular(null)}
      />
      <ConfirmDialog
        active={porQuitar != null}
        title={porQuitar ? `Quitar ${nombreLinea(porQuitar.item)}` : ''}
        description={textoQuitar(porQuitar?.item)}
        note={errorQuitar || null}
        confirmLabel="Quitar"
        tone="danger"
        busy={reversingId != null && reversingId === porQuitar?.tx.id}
        onConfirm={reverseUnits}
        onCancel={() => setPorQuitar(null)}
        titleId="quitar-linea-title"
      >
        {porQuitar && !lineaAPeso(porQuitar.item) && maxUnidades(porQuitar.item) > 1 && (
          <div className="flex items-center gap-3 text-sm">
            <span>Unidades a quitar</span>
            <QuantityCounter
              value={unidades}
              onChange={cambiarUnidades(porQuitar.item)}
              max={maxUnidades(porQuitar.item)}
              label="Unidades a quitar"
            />
            <span className="text-[var(--muted-foreground)]">de {maxUnidades(porQuitar.item)}</span>
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}

export default ClientTransactions;
